/*
==============================================================================
Component ID: EP-CARD-01
Asset Name: External Product Card
Version: 1.2.2

Purpose:
Shared behaviour and privacy-safe tracking for External Product cards.

Features:
- Swipeable inner image galleries on tablet/desktop
- Mobile horizontal gestures are reserved for the parent product carousel
- Desktop hover to second image
- Swipe-safe click suppression
- Pauses/resumes a parent Swiper autoplay where present
- Delegated product_card_click tracking
- Re-initialises safely in Shopify Theme Editor sections

Tracking:
- Event: product_card_click
- Standard fields:
  product_id
  product_handle
  product_title
  brand_name
  product_type
  destination_url
  source
- Additional context:
  feed_source
  card_position
  card_style
  section_id
  block_id

Privacy:
No email, customer ID, user ID or personal information is captured.

Change Log:
v1.2.2
- Renamed external_product_click to product_card_click
- Renamed the tracking hook to data-product-card-click
- Standardised product and placement parameters with other Hazaar product widgets
- Added destination_url, source and product_type
- Renamed source_type reporting parameter to feed_source
- Renamed custom browser event to hazaar:product-card-click
- Added transport_type=beacon for navigation-safe GA delivery
- Preserved existing gallery and swipe suppression behaviour

v1.2.1
- Existing External Product card gallery and click tracking behaviour
==============================================================================
*/

(() => {
  if (window.HazaarExternalProductCard) return;

  const mobileProductCarouselGesture = () =>
    window.matchMedia('(max-width: 749px)').matches;

  const setGalleryIndex = (gallery, nextIndex) => {
    const slides = Array.from(
      gallery.querySelectorAll('[data-gallery-slide]')
    );

    const dots = Array.from(
      gallery.querySelectorAll('[data-gallery-dot]')
    );

    if (slides.length < 2) return;

    const boundedIndex =
      (nextIndex + slides.length) % slides.length;

    gallery.dataset.galleryIndex = String(boundedIndex);

    slides.forEach((slide, index) => {
      slide.classList.toggle(
        'is-active',
        index === boundedIndex
      );
    });

    dots.forEach((dot, index) => {
      dot.classList.toggle(
        'is-active',
        index === boundedIndex
      );
    });
  };

  const getParentSwiper = (gallery) => {
    const swiperComponent =
      gallery.closest('swiper-component');

    if (swiperComponent?.swiper) {
      return swiperComponent.swiper;
    }

    const panel =
      gallery.closest('[data-external-product-panel]');

    return (
      panel
        ?.querySelector('swiper-component')
        ?.swiper || null
    );
  };

  const pauseOuterAutoplay = (gallery) => {
    const swiper = getParentSwiper(gallery);

    if (swiper?.autoplay?.running) {
      swiper.autoplay.stop();

      gallery.dataset.resumeOuterAutoplay =
        'true';
    }
  };

  const resumeOuterAutoplay = (gallery) => {
    if (
      gallery.dataset.resumeOuterAutoplay !==
      'true'
    ) {
      return;
    }

    getParentSwiper(gallery)
      ?.autoplay
      ?.start();

    delete gallery.dataset
      .resumeOuterAutoplay;
  };

  const initialiseGallery = (gallery) => {
    const slides = Array.from(
      gallery.querySelectorAll(
        '[data-gallery-slide]'
      )
    );

    if (
      slides.length < 2 ||
      gallery.dataset.galleryInitialised ===
        'true'
    ) {
      return;
    }

    gallery.dataset.galleryInitialised =
      'true';

    let startX = 0;
    let startY = 0;
    let pointerMoved = false;

    gallery.addEventListener(
      'pointerdown',
      (event) => {
        if (
          mobileProductCarouselGesture()
        ) {
          return;
        }

        startX = event.clientX;
        startY = event.clientY;
        pointerMoved = false;

        pauseOuterAutoplay(gallery);
      }
    );

    gallery.addEventListener(
      'pointermove',
      (event) => {
        if (
          mobileProductCarouselGesture()
        ) {
          return;
        }

        const deltaX =
          event.clientX - startX;

        const deltaY =
          event.clientY - startY;

        if (
          Math.abs(deltaX) > 8 &&
          Math.abs(deltaX) >
            Math.abs(deltaY)
        ) {
          pointerMoved = true;
        }
      }
    );

    const onPointerUp = (event) => {
      if (
        mobileProductCarouselGesture()
      ) {
        return;
      }

      const deltaX =
        event.clientX - startX;

      const deltaY =
        event.clientY - startY;

      if (
        pointerMoved &&
        Math.abs(deltaX) > 35 &&
        Math.abs(deltaX) >
          Math.abs(deltaY)
      ) {
        const currentIndex = Number(
          gallery.dataset.galleryIndex ||
            0
        );

        setGalleryIndex(
          gallery,
          deltaX < 0
            ? currentIndex + 1
            : currentIndex - 1
        );

        gallery.dataset.suppressNextClick =
          'true';
      }

      window.setTimeout(() => {
        resumeOuterAutoplay(gallery);
      }, 1200);
    };

    gallery.addEventListener(
      'pointerup',
      onPointerUp
    );

    gallery.addEventListener(
      'pointercancel',
      onPointerUp
    );

    gallery.addEventListener(
      'mouseenter',
      () => {
        if (
          !window
            .matchMedia('(hover: hover)')
            .matches ||
          slides.length < 2
        ) {
          return;
        }

        pauseOuterAutoplay(gallery);
        setGalleryIndex(gallery, 1);
      }
    );

    gallery.addEventListener(
      'mouseleave',
      () => {
        if (
          !window
            .matchMedia('(hover: hover)')
            .matches
        ) {
          return;
        }

        setGalleryIndex(gallery, 0);
        resumeOuterAutoplay(gallery);
      }
    );
  };

  const init = (root = document) => {
    root
      .querySelectorAll?.(
        '[data-external-product-gallery]'
      )
      .forEach(initialiseGallery);
  };

  const trackClick = (link) => {
    const destinationUrl =
      link.dataset.destinationUrl ||
      link.href ||
      '';

    const eventParameters = {
      product_id:
        link.dataset.productId || '',

      product_handle:
        link.dataset.productHandle || '',

      product_title:
        link.dataset.productTitle || '',

      brand_name:
        link.dataset.brandName || '',

      product_type:
        link.dataset.productType ||
        'external',

      destination_url:
        destinationUrl,

      click_source:
        link.dataset.clickSource ||
        'external_product_carousel',

      feed_source:
        link.dataset.feedSource || '',

      card_position: Number(
        link.dataset.cardPosition || 0
      ),

      card_style:
        link.dataset.cardStyle || '',

      section_id:
        link.dataset.sectionId || '',

      block_id:
        link.dataset.blockId || '',

      transport_type: 'beacon'
    };

    document.dispatchEvent(
      new CustomEvent(
        'hazaar:product-card-click',
        {
          detail: eventParameters
        }
      )
    );

    if (
      typeof window.gtag === 'function'
    ) {
      window.gtag(
        'event',
        'product_card_click',
        eventParameters
      );
    } else {
      window.dataLayer =
        window.dataLayer || [];

      window.dataLayer.push({
        event: 'product_card_click',
        ...eventParameters
      });
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target.closest(
          '[data-product-card-click]'
        );

      if (!link) return;

      const gallery =
        link.querySelector(
          '[data-external-product-gallery]'
        );

      if (
        gallery?.dataset
          .suppressNextClick === 'true'
      ) {
        event.preventDefault();
        event.stopPropagation();

        delete gallery.dataset
          .suppressNextClick;

        return;
      }

      trackClick(link);
    },
    true
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => init(document)
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target || document);
    }
  );

  window.HazaarExternalProductCard = {
    init,
    setGalleryIndex
  };

  init(document);
})();